import type { ProblemDetails } from '@persped/shared';
import { getAccessToken } from '../auth/tokenProvider';
import { ApiError } from './ApiError';

/**
 * Relatív útvonal: a SPA-t ugyanaz a Container App szolgálja ki, mint az
 * API-t (same-origin), fejlesztéskor pedig a Vite proxyzza. Így nincs
 * környezetfüggő base URL, amit el lehetne rontani a deploynál.
 */
const API_PREFIX = '/api/v1';

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
  method?: Method;
  /** JSON törzs. Kizárja egymást a `formData`-val. */
  body?: unknown;
  /** Multipart törzs (fotófeltöltés). */
  formData?: FormData;
  /**
   * Idempotencia-kulcs. A backend KÖTELEZŐVÉ teszi minden írásra
   * (`IDEMPOTENCY_KEY_REQUIRED`), és a kulcsot a FELHASZNÁLÓI MŰVELET
   * pillanatában kell előállítani — nem itt. Ha ez a réteg generálná,
   * minden újrapróbálás új kulcsot kapna, és pont a duplikálást okoznánk,
   * ami ellen az idempotencia véd.
   */
  idempotencyKey?: string;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | undefined>;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_PREFIX}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

const WRITE_METHODS: ReadonlySet<Method> = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';

  if (WRITE_METHODS.has(method) && !options.idempotencyKey) {
    // Fejlesztői hiba, nem futásidejű állapot — hangosan bukjon el itt,
    // ne a szerveren egy 400-zal, ahol már nehezebb visszakövetni.
    throw new Error(`${method} ${path}: hiányzik az idempotencyKey.`);
  }

  const token = await getAccessToken();

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
  };
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      body: options.formData ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
      signal: options.signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw ApiError.network('Nem sikerült elérni a szervert. Ellenőrizd a hálózati kapcsolatot.');
  }

  if (response.status === 204) return undefined as T;

  const isProblem = response.headers.get('content-type')?.includes('application/problem+json');
  const isJson = isProblem || response.headers.get('content-type')?.includes('application/json');
  const payload: unknown = isJson ? await response.json().catch(() => undefined) : undefined;

  if (!response.ok) {
    if (payload && typeof payload === 'object' && 'code' in payload) {
      throw ApiError.fromProblem(payload as ProblemDetails);
    }
    // Nem problem+json (pl. ingress/proxy hiba) — ne találgassunk kódot.
    throw ApiError.network(`Váratlan szerverhiba (HTTP ${response.status}).`);
  }

  return payload as T;
}

/**
 * Új idempotencia-kulcs a felhasználói művelet pillanatában.
 *
 * (A szerver `Idempotent-Replay: true` fejléccel jelzi, ha a tárolt választ
 * játszotta vissza. Ma egyik felület sem használja, ezért a wrapper nem adja
 * ki — ha kell, a `fetch` válaszfejléceiből olvasható ki.)
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
